import { AgentRuleBundle } from "../agent-config-options";
import { renderClaimVerificationSection } from "./claim-verification";
import {
  ISSUE_CLOSE_RULE_REFERENCE,
  ISSUE_CLOSE_SECTION_TITLE,
  renderIssueClosePolicySection,
} from "./issue-close";
import {
  DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  ResolvedIssueDefaults,
  labelsForPhase,
} from "./issue-defaults";
import { renderIssueTypeAssignmentStep } from "./issue-type";
import { renderOriginStampSection } from "./origin-label";
import { DEFAULT_AGENT_PATHS, ResolvedAgentPaths } from "./paths";
import { PROJECT_CONTEXT_READER_SECTION } from "./project-context";
import {
  AGENT_MODEL,
  AGENT_RULE_SCOPE,
  AgentSkill,
  AgentSubAgent,
} from "../types";

/**
 * Verbatim regulation-page template shipped to the consuming project
 * alongside the `/research-regulation` skill. Emitted as
 * `_references/templates/_template-regulation.md` under the skill
 * directory so the research-phase handler resolves it locally without
 * reaching back into configulator.
 *
 * The template is domain-neutral: it captures the structural shape of a
 * regulation page (status, applicability, key requirements, jurisdiction
 * variations, enforcement, penalties, related standards) without
 * hardcoding any specific regulation, regulator, industry, or
 * jurisdiction. Concrete values are filled in by the research phase.
 */
const TEMPLATE_REGULATION = `---
title: "<Regulation Name>"
description: "Research on <Regulation Name> — scope, key requirements, jurisdiction variations, enforcement, and penalties."
regulation: <REGULATION_SLUG>
jurisdiction: <federal | state | provincial | international | local>
industry: <industry-slug or 'platform'>
---

| Field | Value |
|-------|-------|
| **Official name** | <full legal name> |
| **Short name** | <common acronym or short title> |
| **Governing body** | <regulator / enforcement agency> |
| **Jurisdiction** | <federal / state / international / local + country / region> |
| **Status** | <in force / proposed / sunset / superseded> |
| **Effective date** | YYYY-MM-DD |
| **Last reviewed** | YYYY-MM-DD |

## Overview

<2–3 paragraphs on the regulation's purpose, its history, who authored
it, and why it matters to the consuming project's industry or
platform.>

## Scope and Triggers

### Who Must Comply

<Describe the categories of organization or system this regulation
applies to. Cite the triggering section of the regulation.>

### What Activities Trigger It

<Describe the activities, data types, or transactions that bring an
organization in scope. Cite the triggering sections.>

### Exemptions

| Exemption | Scope | Citation |
|-----------|-------|----------|
| <exemption name> | <what is exempt> | <section reference> |

## Key Requirements

Every requirement cites the specific section, article, clause, or
paragraph number. Generic statements without citations are not
acceptable.

| Requirement Area | Summary | Citation |
|------------------|---------|----------|
| <area> | <what is required> | <section / article reference> |

## Jurisdiction Variations

### Base Jurisdiction

| Requirement Area | Base Requirement | Citation |
|------------------|------------------|----------|
| <area> | <what is required> | <section reference> |

### Variations

List jurisdictions where the regulation varies from the base. Skip
jurisdictions that adopt the base unchanged.

#### <Jurisdiction Name>

| Requirement Area | Local Variation | How It Differs | Citation |
|------------------|-----------------|----------------|----------|
| <area> | <local requirement> | <stricter / looser / different scope> | <local law reference> |

If no significant jurisdiction variations exist, replace this section
with \`No significant jurisdiction variations identified.\`

## Enforcement

| Enforcement Mechanism | Description | Citation |
|-----------------------|-------------|----------|
| <mechanism> | <how it is enforced> | <section reference> |

### Enforcement History

<Brief summary of notable enforcement actions or regulator guidance.
Cite each action.>

## Penalties

| Violation Type | Penalty Range | Citation |
|----------------|---------------|----------|
| <type> | <monetary / operational / criminal> | <section reference> |

## Effective Dates and Timelines

| Milestone | Date | Notes |
|-----------|------|-------|
| <event> | <date> | <what changed> |

## Related Regulations

| Regulation | Relationship | Link |
|------------|--------------|------|
| <name> | <supersedes / complements / overlaps / references> | <relative link> |

## Related Standards

Interoperability standards or technical standards that this regulation
mandates or references. Link to standards-research pages when available.

| Standard | Mandated Version | How It's Referenced | Link |
|----------|------------------|---------------------|------|
| <standard> | <version> | <citation> | <relative link> |

If this regulation does not cite any technical standards, replace this
table with \`Not applicable — regulation does not cite technical
standards.\`

## Enforcement Bodies

| Organization | Role | Link |
|--------------|------|------|
| <body> | <rulemaking / enforcement / audit> | <relative link> |

## Open Questions

<Questions a human legal or compliance reviewer must resolve before
downstream requirements are drafted. Keep each item to a sentence.>

## Sources

1. [<Title>](<URL>) — <brief description>

## Revision History

| Date | Author | Changes |
|------|--------|---------|
| YYYY-MM-DD | <author> | Initial research page |
`;

/**
 * Reference files emitted alongside the `/research-regulation` SKILL.md.
 * Currently just the regulation-page template; kept as a
 * `ReadonlyArray` so future reference material (e.g. a source-tier
 * guide) can be added without changing the bundle shape.
 */
const REGULATORY_RESEARCH_REFERENCE_FILES: ReadonlyArray<{
  readonly path: string;
  readonly content: string;
}> = [
  {
    path: "_references/templates/_template-regulation.md",
    content: TEMPLATE_REGULATION,
  },
];

/**
 * Sub-agent that runs jurisdictional regulatory compliance research,
 * deep-dives on individual regulations, and synthesizes impact
 * analyses that hand off to the requirements pipeline as SEC
 * (security & compliance) category seeds.
 *
 * The prompt is deliberately domain-neutral. Concrete regulation
 * names, regulator acronyms, jurisdictions, industry categories, and
 * penalty ranges come from the invoking issue body,
 * `docs/src/content/docs/project-context.md`, or the consuming
 * project's `agentConfig.rules` — not from the bundle.
 */
function buildRegulatoryResearchAnalystSubAgent(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSubAgent {
  return {
    name: "regulatory-research-analyst",
    description:
      "Authors jurisdictional compliance research through a 3-phase pipeline (scan → research → impact). Enumerates applicable regulations by jurisdiction and business activity, researches each in depth, and produces an impact analysis that hands off actionable obligations to the requirements-analyst as req:scan seeds in the SEC category. One phase per session, tracked by regulatory:* GitHub issue labels with filesystem-based durability between phases.",
    model: AGENT_MODEL.POWERFUL,
    maxTurns: 80,
    platforms: { cursor: { exclude: true } },
    prompt: [
      "# Regulatory Research Analyst Agent",
      "",
      "You run a structured jurisdictional compliance research pipeline:",
      "scan applicable regulations by jurisdiction and business activity,",
      "research each regulation in depth, and produce an impact analysis",
      "that hands off actionable obligations to the requirements pipeline",
      "as SEC (security & compliance) category seeds. Each phase runs as",
      "its **own agent session**, triggered by a GitHub issue with a",
      "`regulatory:*` phase label. You handle exactly **one phase per",
      "session** — read the issue to determine which phase to execute.",
      "",
      "This agent is **domain-neutral**. It makes no assumptions about",
      "which regulations matter, which industries they govern, which",
      "jurisdictions are in scope, or which regulators apply. All concrete",
      "vocabulary — regulation names, regulator acronyms, jurisdiction",
      "identifiers, industry categories, and penalty schedules — comes",
      "from the invoking issue body,",
      `\`${paths.docsRoot}/project-context.md\`, or the consuming`,
      "project's configuration.",
      "",
      "This agent **documents** regulatory obligations; it does **not**",
      "interpret them. Legal interpretation, compliance strategy, and risk",
      "assessment are human responsibilities. When regulation text is",
      "ambiguous or regulator guidance conflicts, flag the ambiguity as an",
      "open question and block — never guess at the interpretation.",
      "",
      "Follow your project's shared agent conventions (`AGENTS.md`,",
      "`CLAUDE.md`, or equivalent) for all commit, branch, and PR rules.",
      "",
      "---",
      "",
      ...PROJECT_CONTEXT_READER_SECTION,
      "## Design Principles",
      "",
      "1. **Jurisdiction-aware.** The same regulation may apply differently",
      "   at local, state, federal, and international levels. Document each",
      "   level explicitly — do not collapse variations into one page when",
      "   they materially differ.",
      "2. **Trace to the source.** Every regulatory requirement must cite",
      "   the specific section, article, clause, or paragraph number.",
      '   Generic statements like "regulation X requires encryption" are',
      "   insufficient — the citation must be specific enough that a human",
      "   reviewer can locate the exact passage.",
      "3. **Document, do not interpret.** This agent captures what a",
      "   regulation says, not what it means. When interpretation is",
      "   ambiguous, flag the ambiguity as an open question for legal or",
      "   compliance review. Never guess at regulatory meaning.",
      "4. **Impact-driven.** Regulatory obligations are only useful when",
      "   connected to concrete product requirements. The impact phase is",
      "   the load-bearing step that closes the loop between documented",
      "   regulation and actionable `req:scan` seeds in the SEC category.",
      "5. **Filesystem durability.** Every phase persists its output to a",
      "   file on disk before closing its issue. Downstream phases read",
      "   those files — never rely on session memory.",
      "6. **Issue graph = state machine.** Phase ordering is encoded with",
      "   `Depends on: #N` links between phase issues. Research runs only",
      "   after the scan identifies it; impact runs only after research",
      "   completes.",
      "7. **Generic over specific.** No hardcoded regulation names,",
      "   regulator acronyms, jurisdiction identifiers, or industry",
      "   categories. Use placeholders that the invoking issue or consuming",
      "   project fills in.",
      "8. **Research, not authoring.** This pipeline produces scan reports,",
      "   regulation pages, and impact analyses. It never writes requirement",
      "   documents, capability models, or canonical profiles — those are",
      "   the responsibility of the `requirements-writer`, `bcm-writer`,",
      "   `company-profile`, and `people-profile` bundles. Surfaced work is",
      "   delegated to those bundles via issues.",
      "9. **No fabrication.** Every factual claim must trace to a cited",
      "   source — regulation text, regulator guidance, or a reputable",
      "   compliance publication. When source material is insufficient,",
      "   block the issue rather than filling gaps from general knowledge.",
      "",
      "---",
      "",
      "## Source Credibility",
      "",
      "Regulatory research is held to a stricter evidence standard than",
      "other research pipelines because regulatory errors have real",
      "compliance consequences.",
      "",
      "| Tier | Description | Examples | Usage Rules |",
      "|------|-------------|----------|-------------|",
      "| **Tier 1** | Official, authoritative sources | Regulation text on an official government publisher, regulator guidance documents, peer-reviewed legal publications | A single Tier 1 source is sufficient to mark a claim as supported. Regulatory citations **require** at least one Tier 1 source. |",
      "| **Tier 2** | Reputable professional sources | Major law-firm client alerts, trade-association compliance guides, established industry-press coverage of enforcement actions | Acceptable as supporting context for a claim already grounded in Tier 1. Never sufficient on their own for a regulatory citation. |",
      "| **Tier 3** | Informal sources | Blog posts, community forums, vendor marketing, social media, Wikipedia | Never used for regulatory claims. Acceptable only for colour around enforcement history or adoption context, and always flagged. |",
      "",
      "**Every requirement in a regulation page must have a Tier 1",
      "citation.** No exceptions.",
      "",
      "---",
      "",
      "## State Machine Overview",
      "",
      "Regulatory research flows through **3 phases**:",
      "",
      "```",
      "┌──────────┐   ┌──────────┐   ┌──────────┐",
      "│ 1. SCAN  │──▶│ 2. RESE- │──▶│ 3. IMPACT│",
      "│ Enumerate│   │    ARCH  │   │ Assess   │",
      "│ regs by  │   │ One reg  │   │ impact;  │",
      "│ juris. + │   │ at a     │   │ hand off │",
      "│ activity │   │ time;    │   │ SEC      │",
      "│          │   │ reqs,    │   │ req:scan │",
      "│          │   │ dates,   │   │ seeds    │",
      "│          │   │ penalties│   │          │",
      "└──────────┘   └──────────┘   └──────────┘",
      "```",
      "",
      "**Issue labels encode the phase:**",
      "",
      "| Label | Phase | Session work |",
      "|-------|-------|-------------|",
      "| `regulatory:scan` | 1. Scan | Enumerate regulations applicable to an industry, jurisdiction, or platform. Write a scan report. Create `regulatory:research` issues for each prioritized regulation. 4–8 targeted web searches. |",
      "| `regulatory:research` | 2. Research | Research one regulation in depth — scope, triggers, key requirements, jurisdiction variations, enforcement, penalties. Populate the regulation page from the ship-with template. Create the blocked `regulatory:impact` issue. 6–12 targeted web searches. |",
      "| `regulatory:impact` | 3. Impact | Assess product impact, document capability gaps, hand off each actionable obligation as a `req:scan` issue in the SEC category. No web searches — synthesis and issue creation only. |",
      "",
      "All issues also carry `type:regulatory-research` and a `status:*` label.",
      "",
      "**Issue count per regulation:** 1 research + 1 impact = **2",
      "sessions** (scan is shared across an industry or platform scope).",
      "",
      "**Shortened paths:**",
      "- Research a single regulation with no scan (direct hand-off from",
      "  another agent) → **1 session**.",
      "- Re-assess impact when a regulation page was refreshed → **1",
      "  session**.",
      "",
      "---",
      "",
      "## Configurable Paths",
      "",
      "The pipeline uses these placeholders. Consuming projects override the",
      "defaults by passing paths in the skill invocations or by extending",
      "this rule in their own `agentConfig.rules`.",
      "",
      "| Placeholder | Meaning | Default |",
      "|-------------|---------|---------|",
      `| \`<REGULATORY_ROOT>\` | Root folder for all regulatory-research documents | \`${paths.docsRoot}/regulatory-research/\` |`,
      "| `<REGULATION_DIR>` | Per-scope directory containing regulation pages | `<REGULATORY_ROOT>/<SCOPE_SLUG>/` |",
      "| `<REGULATION_PAGE>` | One-regulation research page | `<REGULATION_DIR>/<REGULATION_SLUG>.md` |",
      "| `<SCOPE_INDEX_PAGE>` | Scope-level index page | `<REGULATION_DIR>/index.md` |",
      "| `<SCAN_DIR>` | Scan reports written during Phase 1 | `<researchRoot>/regulatory/` |",
      "| `<SCOPE_SLUG>` | Short kebab-case slug identifying the industry, jurisdiction, or platform scope | derived from the scan issue |",
      "| `<REGULATION_SLUG>` | Short kebab-case slug identifying one regulation | derived from the research issue |",
      "",
      `If \`${paths.docsRoot}/project-context.md\` specifies a different`,
      "regulatory-research tree, prefer that. Otherwise fall back to the",
      "defaults above.",
      "",
      "---",
      "",
      "## Agent Loop",
      "",
      "Run this loop exactly once per session. Never start a second issue.",
      "",
      "1. Claim one open `type:regulatory-research` issue using phase",
      "   priority: `regulatory:scan` > `regulatory:research` >",
      "   `regulatory:impact`. Within a phase, prefer the oldest ready",
      "   issue (FIFO).",
      "2. Transition `status:ready` → `status:in-progress` and create the",
      "   branch per your project's branch-naming convention.",
      "3. Execute the phase handler that matches the issue's `regulatory:*`",
      "   label.",
      "4. Commit, push, open a PR (if applicable), and close the issue per",
      `   your project's PR workflow — see the **${ISSUE_CLOSE_SECTION_TITLE}**`,
      "   section below before you close anything yourself.",
      "   Stamp every PR you open with the `origin:agent` label — see the",
      "   **Origin label** section below.",
      "",
      "---",
      "",
      ...renderClaimVerificationSection(),
      "",
      "---",
      "",
      ...renderOriginStampSection(),
      "",
      "---",
      "",
      ...renderIssueClosePolicySection(),
      "",
      "---",
      "",
      "## Phase 1: Scan (`regulatory:scan`)",
      "",
      "**Goal:** Enumerate regulations applicable to a given scope —",
      "typically an industry, a jurisdiction, or the consuming project's",
      "platform itself — and produce a prioritized list with downstream",
      "research issues.",
      "",
      "**Budget:** 4–8 targeted web searches. Writing one scan report and",
      "creating `regulatory:research` issues for each prioritized",
      "regulation.",
      "",
      "### Steps",
      "",
      "1. **Read the scan issue body.** Extract:",
      "   - The scope type: industry, jurisdiction, or platform",
      "   - The `<SCOPE_SLUG>` (industry slug, country / region code, or",
      "     `platform`)",
      "   - Authorized input paths — prior industry, customer, or standards",
      "     research that reference the scope",
      "   - Optional: known regulations to include, regulations to exclude,",
      "     and a priority hint",
      "",
      "2. **Read related project context.** Check",
      `   \`${paths.docsRoot}/project-context.md\` for declared`,
      "   jurisdictional scope, industry categories, and any regulations",
      "   already flagged by other agents. Check industry-research,",
      "   customer-research, and standards-research trees for prior",
      "   references to regulations in this scope.",
      "",
      "3. **Read existing regulatory-research pages** under",
      "   `<REGULATION_DIR>`. A scan may land on top of a partially",
      "   populated tree — stub regulation pages, a prior scan report, or",
      "   impact analyses from earlier work. Treat these as inputs, not",
      "   work to redo.",
      "",
      "4. **Execute 4–8 targeted web searches** to enumerate the",
      "   regulatory landscape:",
      "   - Regulations governing the scope at each jurisdiction level",
      "     (local, state / provincial, federal / national, international)",
      "   - Regulator and enforcement-agency identities",
      "   - Recent rulemaking activity or pending regulations",
      "   - Cross-border or supply-chain regulatory overlaps",
      "",
      "   If a search returns low-quality results, try one reformulated",
      "   query before moving on. Never exceed the 8-search cap.",
      "",
      "5. **Write the scan report** to",
      "   `<SCAN_DIR>/regulatory-<SCOPE_SLUG>-<YYYY-MM-DD>.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Regulatory Scan: <Scope>"',
      "   date: YYYY-MM-DD",
      "   parent_issue: <N>",
      "   status: active",
      "   scope: <SCOPE_SLUG>",
      "   ---",
      "",
      "   ## Scope Context",
      "   - **Scope type:** <industry / jurisdiction / platform>",
      "   - **Scope identifier:** <SCOPE_SLUG>",
      "   - **Geographic reach:** <where regulations apply>",
      "",
      "   ## Platform Regulations",
      "   Regulations that apply to the consuming project's software",
      "   platform itself (not a specific industry vertical).",
      "",
      "   | Regulation | Jurisdiction | Why It Applies | Priority |",
      "   |------------|--------------|----------------|----------|",
      "",
      "   ## Regulations by Jurisdiction Level",
      "",
      "   ### International / Cross-Border",
      "   | Regulation | Region | Scope | Priority |",
      "   |------------|--------|-------|----------|",
      "",
      "   ### Federal / National",
      "   | Regulation | Country | Scope | Priority |",
      "   |------------|---------|-------|----------|",
      "",
      "   ### State / Provincial",
      "   | Regulation | State / Province | Scope | Priority |",
      "   |------------|------------------|-------|----------|",
      "",
      "   ### Local / Municipal",
      "   | Regulation | Locality | Scope | Priority |",
      "   |------------|----------|-------|----------|",
      "",
      "   ## Existing Coverage",
      "   - <regulation>: <already researched / issue open / not started>",
      "",
      "   ## Recommendations",
      "   - <regulation> → create research issue (priority: <level>)",
      "   - <regulation> → already covered, no action",
      "   - <regulation> → defer until <condition>",
      "",
      "   ## Sources",
      "   1. [<Title>](<URL>) — <description>",
      "   ```",
      "",
      "6. **Create one `regulatory:research` issue per recommended",
      "   regulation.** Each issue cites the scan report, names the",
      "   regulation, and identifies the jurisdiction. Apply the scan",
      "   issue's priority as the default; escalate if the scope flags a",
      "   higher-priority regulation.",
      "",
      "7. **Create or update the scope index page** at",
      "   `<SCOPE_INDEX_PAGE>` so downstream regulations link back to a",
      "   coherent landing page for the scope. Follow the",
      "   section-index-page contract in the `shared-editing-safety` rule:",
      "   the body must carry a 1–2",
      "   paragraph contextual summary plus a grouped, linked listing of",
      "   every regulation in the scope (e.g. by jurisdiction). No body",
      "   `# Heading` — the frontmatter `title:` already renders as the",
      "   page H1.",
      "",
      "8. **Commit and push** the scan report and the scope index page.",
      `   Close the scan issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 2: Research (`regulatory:research`)",
      "",
      "**Goal:** Research one regulation in depth and populate the",
      "regulation page with scope, triggers, key requirements, jurisdiction",
      "variations, enforcement, and penalties.",
      "",
      "**Budget:** 6–12 targeted web searches (6–8 for core requirements,",
      "plus 0–4 for jurisdiction variations). Writes one regulation page,",
      "creates the blocked impact issue, and may create",
      "`company:research` issues for enforcement bodies not yet profiled.",
      "",
      "### Steps",
      "",
      "1. **Read the scan report** for context on this regulation's",
      "   priority and scope.",
      "",
      "2. **Read the regulation-page template** at",
      "   `.claude/skills/research-regulation/_references/templates/_template-regulation.md`.",
      "   The template is authoritative — copy its structure verbatim into",
      "   the regulation page, then populate each section from your",
      "   research.",
      "",
      "3. **Read existing regulation pages** in the same `<REGULATION_DIR>`",
      "   to follow consistent structure and avoid duplication.",
      "",
      "4. **Execute 6–8 core web searches** for the regulation's",
      "   requirements:",
      "   - Official regulation text on an authoritative publisher",
      "   - Regulator guidance documents",
      "   - Compliance summaries from reputable legal sources",
      "   - Enforcement history and notable actions",
      "   - Recent amendments or proposed changes",
      "   - Implementation timelines",
      "   - Relationship to other regulations",
      "",
      "   If a search returns low-quality results, try one reformulated",
      "   query before moving on.",
      "",
      "5. **Execute up to 4 jurisdiction searches** if the regulation has",
      "   meaningful jurisdiction variations (state / provincial,",
      "   international equivalents, local variations). Skip this step if",
      "   the regulation applies uniformly across its scope.",
      "",
      "6. **Populate the regulation page** at `<REGULATION_PAGE>` by",
      "   copying the template verbatim and filling each section. Every",
      "   requirement in the `Key Requirements` table must cite a specific",
      "   section, article, or paragraph number. Every jurisdiction",
      "   variation must cite the local law reference.",
      "",
      "7. **Create the `regulatory:impact` issue** blocked on this",
      "   research issue (`Depends on: #<this-issue>`). Its body references",
      "   the regulation page and lists the capability gaps the impact",
      "   phase must assess.",
      "",
      "8. **Create `company:research` issues** for enforcement bodies or",
      "   regulator agencies that do not already have a canonical profile",
      "   or an open `company:research` issue. The `company-profile`",
      "   bundle owns the downstream profile; this agent only hands off.",
      "   Each issue must carry:",
      "",
      "   - `type:company-profile`",
      "   - `company:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "company:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "company:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "9. **Verify all handoffs were created** before committing.",
      "",
      "10. **Commit and push** the regulation page. Close the research",
      `    issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 3: Impact (`regulatory:impact`)",
      "",
      "**Goal:** Assess how the regulation affects the consuming project —",
      "which capabilities are affected, which customer or industry segments",
      "are in scope, and which obligations must be translated into",
      "requirements. Hand off each actionable obligation as a `req:scan`",
      "seed that becomes an SEC requirement in the writer pipeline.",
      "",
      "**Budget:** No web searches. Pure synthesis from the regulation",
      "page plus project context, with issue creation for downstream",
      "handoffs.",
      "",
      "### Steps",
      "",
      "1. **Read the regulation page** with full requirements and",
      "   jurisdiction variations.",
      "",
      `2. **Read project context** (\`${paths.docsRoot}/project-context.md\`)`,
      "   to identify which capability areas the consuming project maps to.",
      "   Read capability-model and business-model trees if present for",
      "   reverse mappings.",
      "",
      "3. **Edit the regulation page** to append an `## Impact` section:",
      "",
      "   ```markdown",
      "   ## Impact",
      "",
      "   ### Platform Compliance",
      "   | Requirement Area | Product Component Affected | Compliance Approach |",
      "   |------------------|---------------------------|---------------------|",
      "   | <area from Key Requirements> | <component or capability> | <how the product should comply> |",
      "",
      "   ### Customer Compliance Support",
      "   | Requirement Area | What Customers Need | What the Product Should Provide |",
      "   |------------------|---------------------|---------------------------------|",
      "   | <area> | <customer obligation> | <feature or capability> |",
      "",
      "   ### Capability Gaps",
      "   Capabilities the consuming project needs but does not yet have",
      "   to support compliance:",
      "",
      "   | Gap | Regulation Citation | Priority | Affected Components |",
      "   |-----|---------------------|----------|---------------------|",
      "   | <gap description> | <citation> | <high / medium / low> | <component list> |",
      "",
      "   ### Affected Segments",
      "   | Segment | How Affected |",
      "   |---------|--------------|",
      "   | <segment name> | <how the regulation affects this segment> |",
      "",
      "   ## Traceability",
      "   - **Source regulation:** <citation>",
      "   - **Affected segments:** <relative links>",
      "   - **Standards implementing:** <relative links> (if applicable)",
      "",
      "   **Requirements created** (one per line, ascending issue-number",
      "   order — append your own entries only):",
      "",
      "   - #<N> — <PREFIX> req:scan",
      "   ```",
      "",
      "   The **Requirements created** block is an **append-only,",
      "   one-issue-per-line, deterministically sorted** list — it is a",
      "   merge-conflict hotspot because sibling `regulatory:impact`",
      "   sessions land requirement entries into the same regulation page",
      "   concurrently. Maintain it under the following discipline (the",
      "   same discipline the `shared-editing-safety` rule prescribes for",
      "   shared registries, applied here to this one list section — the",
      "   surrounding regulation page is a hand-authored document, not a",
      "   registry, so only this section is governed):",
      "",
      "   - **One entry per line.** Write each requirement you created as",
      "     its own new `- #<N> — <PREFIX> req:scan` bullet. Never collapse",
      "     entries into a single comma-separated",
      "     `**Requirements created:** #N, #M` line — that single mutable",
      "     line is the original conflict cause and always conflicts when",
      "     two sessions touch it.",
      "   - **Ascending issue-number order.** Insert your bullet in the",
      "     position that keeps the list sorted by issue number. A",
      "     single-line bullet insert into a sorted list rarely truly",
      "     conflicts, so git auto-merges concurrent sibling inserts.",
      "   - **Append-only.** Never rewrite, reorder, or merge existing",
      "     bullets. If a legacy comma line",
      "     (`**Requirements created:** #N, #M`) already exists on the",
      "     page, leave it untouched and add your entries as per-line",
      "     bullets below it — do **not** rewrite the legacy line, because",
      "     rewriting it is itself a conflict.",
      "   - **Defer the commit of this section to the final pre-push",
      "     step.** Pull the latest default branch before inserting, insert",
      "     only your own entry, and if a concurrent sibling already landed",
      "     a bullet, rebase and re-insert. Commit this shared list section",
      "     on its own at the end, then push immediately.",
      "",
      "4. **Create `req:scan` issues** for each capability gap identified.",
      "   Each issue hands off to the `requirements-analyst` bundle so its",
      "   scan phase can deduplicate and open `req:write` issues in the",
      "   SEC category. Each issue must carry:",
      "",
      "   - `type:requirement`",
      "   - `req:scan`",
      `   - \`priority:${labelsForPhase(issueDefaults, "req:scan").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "req:scan").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "   The body must also:",
      "   - Identify the target category as **SEC** (security & compliance)",
      "     in the body so the downstream writer phase picks up the right",
      "     template",
      "   - Link back to the regulation page and cite the triggering",
      "     requirement",
      "   - Escalate the priority above the bundle default when the",
      "     regulation is already in force and the gap is unmitigated",
      "",
      "5. **Create `company:research` issues** for any enforcement body",
      "   mentioned on the regulation page that does not already have a",
      "   canonical profile or an open `company:research` issue. Each",
      "   issue must carry:",
      "",
      "   - `type:company-profile`",
      "   - `company:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "company:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "company:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "6. **Create `people:research` issues** for notable regulatory",
      "   leaders (commissioners, agency directors, lead policy authors)",
      "   mentioned on the regulation page. Keep this list small — at most",
      "   three individuals per impact session — and skip anyone already",
      "   profiled or queued. Each issue must carry:",
      "",
      "   - `type:people-profile`",
      "   - `people:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "people:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "people:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "7. **Commit and push** the updated regulation page. Close the",
      `   impact issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Coordination with Other Agents",
      "",
      "### Upstream: research-pipeline, industry-discovery, standards-research",
      "",
      "Industry, research-pipeline, and standards-research deliverables may",
      "reference regulations without scoping them. A human opens a",
      "`regulatory:scan` issue for the relevant scope to start the",
      "pipeline. This agent never auto-creates `regulatory:scan` issues —",
      "scan is a human-authored starting point.",
      "",
      "### Downstream: requirements-analyst bundle",
      "",
      "Phase 3 (Impact) hands off actionable obligations to the",
      "`requirements-analyst` bundle via `req:scan` issues. The body of",
      "each handoff issue identifies the target category as **SEC**",
      "(security & compliance) so the downstream `requirements-writer`",
      "phase picks up the right template. This agent never writes",
      "requirement documents itself.",
      "",
      "### Downstream: company-profile bundle",
      "",
      "This agent creates `company:research` issues for enforcement bodies",
      "and regulator agencies surfaced during research (Phase 2) or impact",
      "(Phase 3). The `company-profile` bundle's Phase 1 (research)",
      "consumes those issues and produces the canonical profile. This",
      "agent never writes canonical company profiles itself.",
      "",
      "### Downstream: people-profile bundle",
      "",
      "This agent creates `people:research` issues for notable regulatory",
      "leaders surfaced during impact analysis. The `people-profile`",
      "bundle's Phase 1 (research) consumes those issues and produces the",
      "canonical profile. This agent never writes canonical people",
      "profiles itself.",
      "",
      "### File Boundaries",
      "",
      "- **Reads:**",
      "  - `<REGULATORY_ROOT>/` — prior scan reports, regulation pages, and",
      "    impact analyses",
      "  - `<SCAN_DIR>/` — scan reports",
      "  - `<docsRoot>/` — project context, industry-research,",
      "    customer-research, and standards-research trees for framing",
      "    context",
      "- **Writes:**",
      "  - `<SCAN_DIR>/regulatory-<SCOPE_SLUG>-<YYYY-MM-DD>.md` (Phase 1)",
      "  - `<SCOPE_INDEX_PAGE>` (Phase 1)",
      "  - `<REGULATION_PAGE>` (Phases 2 and 3 — Phase 3 appends `## Impact`)",
      "- **Never writes to:**",
      "  - Any canonical company-profile or people-profile path (the",
      "    `company-profile` and `people-profile` bundles own those)",
      "  - `<BCM_DOC_ROOT>/` (the `bcm-writer` bundle owns this)",
      "  - `<REQUIREMENTS_ROOT>/` (the `requirements-writer` bundle owns",
      "    this)",
      "  - `<BUSINESS_MODELS_ROOT>/` (the `business-models` bundle owns",
      "    this)",
      "  - `<STANDARDS_ROOT>/` (the `standards-research` bundle owns this)",
      "",
      "---",
      "",
      "## Blocked Issues",
      "",
      "Follow your project's shared Blocked Issues procedure. Additional",
      "block reasons specific to regulatory research:",
      "",
      "- **Regulation text behind a paywall or login.** The agent cannot",
      "  access the authoritative content.",
      "- **Conflicting regulator guidance.** Two or more authoritative",
      "  sources disagree on a material requirement; human legal review is",
      "  needed.",
      "- **Pending or proposed regulation.** Rapidly changing draft content",
      "  that would be out-of-date before the PR merges.",
      "- **Ambiguous applicability.** The agent cannot determine whether a",
      "  regulation applies to the consuming project or its customers",
      "  without a human decision.",
      "- **Impact issue arrived before research completed.** Phase 3 blocks",
      "  on the Phase 2 research issue closing.",
      "",
      "---",
      "",
      "## Rules",
      "",
      "- **One phase per session.** Never run two phases back-to-back in a",
      "  single session.",
      "- **One regulation per research session.** Phase 2 is scoped to",
      "  exactly one regulation — never research multiple regulations at",
      "  once.",
      "- **Persist before closing.** Every phase must write its output file",
      "  before closing its issue.",
      "- **Search budgets are caps, not targets.** Do not exceed 8",
      "  searches in scan, 12 in research, or 0 in impact.",
      "- **Cite specific sections.** Every regulatory requirement must cite",
      "  the specific section, article, clause, or paragraph number.",
      "- **No legal interpretation.** Document what the regulation says,",
      "  not what it means. When interpretation is ambiguous, flag the",
      "  ambiguity as an open question and block.",
      "- **Tier 1 required for citations.** Every requirement in the Key",
      "  Requirements table needs at least one Tier 1 source.",
      "- **No fabrication.** Every claim in a scan report, regulation",
      "  page, or impact analysis must trace to a cited source. Do not",
      "  populate from general knowledge alone.",
      "- **Relative links only.** Cross-references between docs use",
      "  relative markdown paths, never absolute `https://github.com/...`",
      "  URLs.",
      "- **Never cross agent boundaries.** Do not write canonical company",
      "  or people profiles, requirement documents, capability models,",
      "  business-model canvases, or standards-research pages. Hand off",
      "  surfaced work via `company:research`, `people:research`, or",
      "  `req:scan` issues.",
    ].join("\n"),
  };
}

/** Skill that kicks off the scan phase of a regulatory-research campaign. */
function buildScanRegulatoryLandscapeSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "scan-regulatory-landscape",
    description:
      "Kick off the scan phase of a regulatory-research campaign. Creates a regulatory:scan issue for the supplied scope (industry, jurisdiction, or platform) and dispatches Phase 1 of the regulatory-research-analyst, which writes a scan report and creates downstream regulatory:research issues.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "regulatory-research-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Scan Regulatory Landscape",
      "",
      "Kick off a regulatory-research campaign for a given scope — an",
      "industry, a jurisdiction, or the consuming project's platform",
      "itself. Creates a `regulatory:scan` issue carrying the scope",
      "identifier and dispatches Phase 1 (Scan) in the",
      "regulatory-research-analyst agent.",
      "",
      "## Usage",
      "",
      "/scan-regulatory-landscape <scope-slug>",
      "",
      "Optional extensions in the issue body:",
      "- `scope_type: <industry | jurisdiction | platform>` — defaults to",
      "  `industry`",
      "- `scope_name: <human-readable name>` — defaults to the slug",
      "  prettified",
      "- `jurisdictions: <list>` — specific jurisdiction levels to enumerate",
      "- `include_regulations: <list>` — regulations that must be covered",
      "- `exclude_regulations: <list>` — regulations to skip",
      "- `sources: <list>` — additional authorized input paths (prior",
      "  industry, customer, or standards research that references the",
      "  scope)",
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "outputs land under:",
      "",
      `- Scan report: \`${paths.researchRoot}/regulatory/regulatory-<scope-slug>-<YYYY-MM-DD>.md\``,
      `- Scope index page: \`${paths.docsRoot}/regulatory-research/<scope-slug>/index.md\``,
      `- Regulation pages (Phase 2): \`${paths.docsRoot}/regulatory-research/<scope-slug>/<regulation-slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `regulatory:scan` issue with `type:regulatory-research`,",
      `   \`priority:${labelsForPhase(issueDefaults, "regulatory:scan").priority}\`, and \`status:${labelsForPhase(issueDefaults, "regulatory:scan").status}\`. The body must include the`,
      "   scope slug, the scope type, and any known regulations to include",
      "   or exclude.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 1 (Scan) of the regulatory-research-analyst agent.",
      "3. Phase 1 writes the scan report and creates one",
      "   `regulatory:research` issue per prioritized regulation.",
      "",
      "## Output",
      "",
      "- A scan report under the project's research directory, listing",
      "  applicable regulations by jurisdiction level and recommended",
      "  research priorities.",
      "- One `regulatory:research` issue per prioritized regulation.",
      "- A scope index page under the regulatory-research tree.",
    ].join("\n"),
  };
}

/** Skill that kicks off the research phase for one regulation. */
function buildResearchRegulationSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "research-regulation",
    description:
      "Kick off the research phase for one regulation. Creates a regulatory:research issue for the supplied scope and regulation and dispatches Phase 2 of the regulatory-research-analyst, which populates the regulation page with scope, triggers, key requirements, jurisdiction variations, enforcement, and penalties using the ship-with regulation-page template.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "regulatory-research-analyst",
    platforms: { cursor: { exclude: true } },
    referenceFiles: REGULATORY_RESEARCH_REFERENCE_FILES,
    instructions: [
      "# Research Regulation",
      "",
      "Author a regulation page for one regulation. Creates a",
      "`regulatory:research` issue and dispatches Phase 2 (Research) in",
      "the regulatory-research-analyst agent.",
      "",
      "Most campaigns do not invoke this skill directly — Phase 1 (Scan)",
      "creates research issues automatically. Use this skill when:",
      "",
      "- A single regulation needs refreshed research without a full scan",
      "- A new regulation was published after the last scan and needs to",
      "  be folded into the tree",
      "- The scan phase deferred a regulation and the blocker is now",
      "  resolved",
      "- Another agent (e.g. `standards-research`) flagged a mandated",
      "  regulation that needs its own page",
      "",
      "## Usage",
      "",
      "/research-regulation <scope-slug>:<regulation-slug>",
      "",
      "Optional extensions in the issue body:",
      "- `scan_report: <path>` — prior scan-phase report (optional when",
      "  invoked manually)",
      "- `jurisdiction: <federal | state | international | local>` —",
      "  primary jurisdiction for the regulation",
      "- `sources: <list>` — additional authorized input paths",
      "",
      "## Regulation-Page Template",
      "",
      "This skill ships a verbatim regulation-page template at",
      "`.claude/skills/research-regulation/_references/templates/_template-regulation.md`.",
      "The Phase 2 handler reads that file as its authoritative structure —",
      "every regulation page copies the template and fills scope, triggers,",
      "key requirements, jurisdiction variations, enforcement, penalties,",
      "and traceability sections.",
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "the regulation page lands at:",
      "",
      `\`${paths.docsRoot}/regulatory-research/<scope-slug>/<regulation-slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `regulatory:research` issue with",
      `   \`type:regulatory-research\`, \`priority:${labelsForPhase(issueDefaults, "regulatory:research").priority}\`, and`,
      `   \`status:${labelsForPhase(issueDefaults, "regulatory:research").status}\`. The body must include the scope slug and the`,
      "   regulation slug.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 2 (Research) of the regulatory-research-analyst",
      "   agent. The agent runs 6–12 targeted web searches and populates",
      "   the regulation page.",
      "",
      "## Output",
      "",
      "- A populated regulation page with scope, triggers, key",
      "  requirements (with specific section citations), jurisdiction",
      "  variations, enforcement, penalties, related regulations, and a",
      "  cited sources section.",
      "- One blocked `regulatory:impact` issue for the downstream Phase 3.",
      "- `company:research` issues for enforcement bodies that do not yet",
      "  have a canonical profile.",
    ].join("\n"),
  };
}

/** Skill that kicks off the impact phase for one regulation. */
function buildImpactRegulationSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "impact-regulation",
    description:
      "Kick off the impact phase for one regulation. Creates a regulatory:impact issue for the supplied regulation and dispatches Phase 3 of the regulatory-research-analyst, which assesses product impact, documents capability gaps, and hands off each actionable obligation as a req:scan seed in the SEC (security & compliance) category for the requirements-analyst bundle to consume.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "regulatory-research-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Assess Regulation Impact",
      "",
      "Assess the impact of one regulation on the consuming project and",
      "hand off each actionable obligation to the requirements pipeline",
      "as a SEC (security & compliance) `req:scan` seed. Creates a",
      "`regulatory:impact` issue and dispatches Phase 3 (Impact) in the",
      "regulatory-research-analyst agent.",
      "",
      "Most campaigns do not invoke this skill directly — Phase 2",
      "(Research) creates the impact issue automatically, blocked on the",
      "research issue that spawned it. Use this skill when:",
      "",
      "- A regulation page was refreshed and the impact analysis needs a",
      "  re-run",
      "- A human authored the regulation page and needs the impact",
      "  synthesized separately",
      "- The impact phase was deferred and the research is now landed",
      "",
      "## Usage",
      "",
      "/impact-regulation <scope-slug>:<regulation-slug>",
      "",
      "Required extensions in the issue body:",
      "- `regulation_page: <path>` — link to the research page being",
      "  assessed",
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "the impact section is appended to the regulation page at:",
      "",
      `\`${paths.docsRoot}/regulatory-research/<scope-slug>/<regulation-slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `regulatory:impact` issue with",
      `   \`type:regulatory-research\`, \`priority:${labelsForPhase(issueDefaults, "regulatory:impact").priority}\`, and`,
      `   \`status:${labelsForPhase(issueDefaults, "regulatory:impact").status}\` (or \`status:blocked\` when still dependent on a`,
      "   Phase 2 research issue). Body must include the regulation page",
      "   path.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 3 (Impact) of the regulatory-research-analyst",
      "   agent. No web searches — pure synthesis from the regulation",
      "   page and project context.",
      "",
      "## Output",
      "",
      "- An `## Impact` section appended to the regulation page covering",
      "  platform compliance, customer compliance support, capability",
      "  gaps, and affected segments.",
      "- One `req:scan` issue (consumed by the `requirements-analyst`",
      "  bundle) per capability gap identified. Each handoff carries the",
      "  SEC category in the body so the downstream `requirements-writer`",
      "  phase picks up the `security/` template.",
      "- Optional `company:research` and `people:research` issues for",
      "  enforcement bodies and regulatory leaders not yet profiled.",
    ].join("\n"),
  };
}

/**
 * Regulatory-research bundle — enabled by default.
 *
 * Consuming projects can disable it with
 * `excludeBundles: ["regulatory-research"]`. `appliesWhen` always
 * returns `true` per the workflow-bundle peer-present assumption.
 *
 * Ships a sub-agent (`regulatory-research-analyst`), three
 * user-invocable skills (`/scan-regulatory-landscape`,
 * `/research-regulation`, `/impact-regulation`), a regulation-page
 * template (emitted alongside the research skill), and
 * `type:regulatory-research` plus `regulatory:*` phase labels.
 *
 * The bundle sits downstream of `research-pipeline`,
 * `industry-discovery`, and `standards-research` (which surface the
 * need for regulatory research) and hands off actionable obligations
 * to the `requirements-analyst` bundle via `req:scan` issues that
 * become SEC (security & compliance) requirements in the writer
 * pipeline, and canonical profiles to `company-profile` and
 * `people-profile` for enforcement bodies and regulatory leaders.
 */
export function buildRegulatoryResearchBundle(
  paths: ResolvedAgentPaths = DEFAULT_AGENT_PATHS,
  issueDefaults: ResolvedIssueDefaults = DEFAULT_RESOLVED_ISSUE_DEFAULTS,
): AgentRuleBundle {
  return {
    name: "regulatory-research",
    description:
      "Regulatory compliance research pipeline: scan, research, impact. 3 phases with regulatory:* phase labels. Enumerates jurisdictional regulations by industry and activity, researches each in depth with specific section citations, and hands off actionable obligations to the requirements-analyst bundle as req:scan seeds that become SEC (security & compliance) requirements. Enabled by default; domain-neutral; documents obligations without interpreting them; filesystem-durable between phases.",
    appliesWhen: () => true,
    rules: [
      {
        name: "regulatory-research-workflow",
        description:
          "Describes the 3-phase regulatory-research pipeline, the regulatory:* label taxonomy, and the handoff boundaries with requirements-analyst (SEC req:scan seeds), company-profile, and people-profile.",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Regulatory Research Workflow",
          "",
          "Use `/scan-regulatory-landscape <scope-slug>` to kick off a",
          "regulatory-research campaign. The pipeline runs in 3 phases —",
          "scan, research, impact — each tracked by its own GitHub issue",
          "labeled `regulatory:scan`, `regulatory:research`, or",
          "`regulatory:impact`. All issues carry",
          "`type:regulatory-research`.",
          "",
          "Two additional user-invocable skills drive the downstream",
          "phases directly when the default pipeline needs to be entered",
          "mid-stream:",
          "",
          "- `/research-regulation <scope>:<regulation>` — author one",
          "  regulation page (normally created by the scan phase",
          "  automatically).",
          "- `/impact-regulation <scope>:<regulation>` — synthesize",
          "  product impact and hand off SEC `req:scan` seeds to the",
          "  `requirements-analyst` bundle (normally created by the",
          "  research phase automatically).",
          "",
          "The bundle produces **scan reports, regulation pages, and",
          "impact analyses**. It never writes requirement documents,",
          "capability models, canonical profiles, business-model canvases,",
          "or standards-research pages — surfaced work is delegated to the",
          "`requirements-analyst`, `company-profile`, and `people-profile`",
          "bundles via `req:scan`, `company:research`, and",
          "`people:research` issues respectively. Each `req:scan` handoff",
          "targets the **SEC (security & compliance)** category so the",
          "downstream `requirements-writer` phase picks up the right",
          "template.",
          "",
          "Consumer-specific regulatory landscapes (which industries,",
          "jurisdictions, and regulations to cover) are supplied per-",
          `project via \`${paths.docsRoot}/project-context.md\` or the`,
          "invoking issue body. The bundle is domain-neutral — no",
          "regulations, regulators, or jurisdictions are hardcoded.",
          "",
          "The bundle **documents** regulatory obligations; it does",
          "**not** interpret them. Legal interpretation, compliance",
          "strategy, and risk assessment are human responsibilities. When",
          "regulation text is ambiguous or regulator guidance conflicts,",
          "the agent flags the ambiguity as an open question and blocks —",
          "it never guesses at the interpretation.",
          "",
          "See the `regulatory-research-analyst` agent definition for",
          "full workflow details, default paths, configurable",
          "placeholders, and phase-by-phase instructions.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],
    skills: [
      buildScanRegulatoryLandscapeSkill(paths, issueDefaults),
      buildResearchRegulationSkill(paths, issueDefaults),
      buildImpactRegulationSkill(paths, issueDefaults),
    ],
    subAgents: [buildRegulatoryResearchAnalystSubAgent(paths, issueDefaults)],
    labels: [
      {
        name: "type:regulatory-research",
        color: "5319E7",
        description:
          "Researches a regulatory obligation: jurisdictional scan, deep-dive, or impact analysis",
      },
      {
        name: "regulatory:scan",
        color: "C5DEF5",
        description:
          "Phase 1: enumerate regulations for an industry/jurisdiction/scope; create research issues",
      },
      {
        name: "regulatory:research",
        color: "BFDADC",
        description:
          "Phase 2: research one regulation in depth (scope, triggers, requirements, enforcement)",
      },
      {
        name: "regulatory:impact",
        color: "D4C5F9",
        description:
          "Phase 3: assess product impact and hand off SEC req:scan seeds to requirements-analyst",
      },
    ],
  };
}

/**
 * Default-paths instance of the regulatory-research bundle, preserved
 * for backward compatibility with consumers that import the const
 * directly. The factory above is the canonical entry point when a
 * consumer supplies `AgentConfigOptions.paths`.
 */
export const regulatoryResearchBundle: AgentRuleBundle =
  buildRegulatoryResearchBundle();
